import { useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { api } from "../api/client";
import type { ReleaseEntry, ReleaseNotes } from "../api/types";
import { Motion, Presence } from "../components/presence";
import { lang, t } from "../i18n/i18n";

/** Numéro de version de Generex Web en grisé au bas de la barre latérale (demande d'Alexis du 28/10/2026) ; au
 *  survol (ou à l'appui, sur tablette), les nouveautés des dernières versions, la plus récente en tête. */

export const RELEASE_NOTES_KEY = ["release-notes"] as const;
/** Délai avant de refermer la fiche quand le pointeur la quitte (le temps de passer du numéro à la fiche). */
const CLOSE_DELAY_MS = 180;
const CARD_WIDTH = 320;

export function useReleaseNotes() {
  return useQuery<ReleaseNotes>({ queryKey: RELEASE_NOTES_KEY, queryFn: api.releaseNotes, staleTime: 10 * 60_000 });
}

/** Lignes d'une version dans la langue lue (repli sur le français si l'anglais manque). */
export function releaseLines(entry: ReleaseEntry, language: string): string[] {
  return language === "en" && entry.en.length ? entry.en : entry.fr;
}

export function SidebarVersion() {
  const { data } = useReleaseNotes();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ left: number; bottom: number } | null>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = () => {
    window.clearTimeout(timer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const rect = anchor.current.getBoundingClientRect();
    // Fiche centrée sur le numéro (lui-même centré dans la barre), gardée dans la fenêtre.
    const centre = rect.left + rect.width / 2 - CARD_WIDTH / 2;
    const left = Math.max(8, Math.min(centre, window.innerWidth - CARD_WIDTH - 8));
    setPlace({ left, bottom: window.innerHeight - rect.top + 6 });
  }, [open]);

  if (!data?.version) return null;
  const language = lang();
  const versions = data.versions.filter((v) => releaseLines(v, language).length);
  return (
    <div className="flex justify-center px-3 pb-2" data-sidebar-version="">
      <button
        ref={anchor}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onMouseEnter={show}
        onMouseLeave={hideSoon}
        onFocus={show}
        onBlur={hideSoon}
        onClick={() => setOpen((o) => !o)}
        className="rounded px-1.5 py-0.5 text-meta text-ink-muted tabular-nums transition-colors hover:text-ink"
      >
        {t("Version {n}", { n: data.version })}
      </button>
      {createPortal(
        <Presence show={open && place !== null && versions.length > 0}>
          {open && place && (
            <Motion
              origin="bottom"
              role="dialog"
              aria-label={t("Nouveautés de Generex Web")}
              onMouseEnter={show}
              onMouseLeave={hideSoon}
              style={{ position: "fixed", left: place.left, bottom: place.bottom, width: CARD_WIDTH }}
              className="z-50 max-h-[60vh] overflow-y-auto rounded-xl border border-line bg-surface p-4 shadow-float"
            >
              {versions.map((entry, i) => (
                <section key={entry.version} className={i ? "mt-3 border-t border-line pt-3" : undefined}>
                  <h3 className="mb-1.5 text-ui font-semibold text-ink">
                    {t("Nouveautés de la version {n}", { n: entry.version })}
                  </h3>
                  <ul className="space-y-1.5">
                    {releaseLines(entry, language).map((line, j) => (
                      <li key={j} className="flex gap-2 text-ui leading-snug text-ink">
                        <span aria-hidden className="mt-[7px] size-1 shrink-0 rounded-full bg-ink-muted" />
                        <span className="min-w-0">{line}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </Motion>
          )}
        </Presence>,
        document.body,
      )}
    </div>
  );
}
